import { createFileRoute } from "@tanstack/react-router";
import { loadStandalonePage, loadSliderCustomers } from "~/utils/content-fns";
import { BlockRenderer } from "~/components/blocks";
import { seo, seoLinks } from "~/utils/seo";
import { Button } from "~/components/Button";

export const Route = createFileRoute("/")({
  loader: async () => {
    const [page, sliderCustomers] = await Promise.all([
      loadStandalonePage({ data: { path: "_index.md" } }),
      loadSliderCustomers(),
    ]);
    const blocks = (page.blocks as Array<{ block: string; [key: string]: unknown }>) || [];

    // Inject dynamic slider customer data into the home-hero-2026 block
    // Hugo pulls this from customer pages with `slider` param — not from homepage frontmatter
    const enrichedBlocks = blocks.map((block) => {
      if (block.block !== "home-hero-2026") return block;
      return {
        ...block,
        _sliderCustomers: sliderCustomers,
      };
    });

    return {
      title: (page.title as string) || "C1",
      seo_title: page.seo_title as string | undefined,
      seo_description: page.seo_description as string | undefined,
      featured_image: page.featured_image as string | undefined,
      blocks: enrichedBlocks,
    };
  },
  head: ({ loaderData }) => ({
    meta: seo({
      title: loaderData?.seo_title || "C1 | Access for the agentic era",
      description:
        loaderData?.seo_description ||
        "C1 is the identity security platform that helps security, IT, and GRC teams secure every identity.",
      image: loaderData?.featured_image,
      url: "/",
    }),
    links: seoLinks({ url: "/" }),
  }),
  component: Home,
});

function Home() {
  const page = Route.useLoaderData();

  if (page.blocks && page.blocks.length > 0) {
    return <BlockRenderer blocks={page.blocks} />;
  }

  // Fallback if no blocks (shouldn't happen with content imported)
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center gap-8 px-6 text-center">
      <h1 className="text-5xl font-bold text-charcoal">C1.ai</h1>
      <p className="max-w-xl text-lg text-gray-60">
        Identity security platform. Site migration in progress.
      </p>
      <Button href="/lp/request-demo" variant="teal">
        Book a demo
      </Button>
    </div>
  );
}
